import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';

import { styles } from './styles';
import { SAMPLE_MADE_WITH_ITEMS } from './constants';
import {
  CheckMarkCell,
  InfoRow,
  WrappingInfoRow,
  TableRow,
  CompactTableCell,
  PDFLogo,
  ProductImage,
  PDFBarcode,
} from './PDFComponents';
import {
  formatSampleTypeDisplay,
  getContainerStyle,
  formatDateUTC,
  getFabricSections,
} from './utils';
import { getImageUrlForPDF } from '../utils/imageUtils';
import { SampleTagResponse } from '../types';

export function ProtoSamplePDF({ tagData }: { tagData: SampleTagResponse }) {
  const sampleType = tagData?.type_of_sample_tag;
  const styleNumber = tagData?.style_number;
  const developmentType = tagData?.type_of_development;

  const productImageUrl = getImageUrlForPDF(
    tagData?.product_image,
    tagData?.productImageDataUrl,
  );

  const containerStyle = getContainerStyle(sampleType, developmentType);
  const barcodeValue = tagData?.sample_tag_lpn || tagData?.id;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={containerStyle}>
          <View
            style={[styles.mainContent, { minHeight: 180, marginBottom: 8 }]}
          >
            {/* Left Column - All Content */}
            <View style={styles.leftColumn}>
              {/* Logo and Date Header */}
              <View style={styles.logoDateHeader}>
                <PDFLogo division={tagData?.division} />
                <Text
                  style={{
                    fontSize: 7,
                    marginLeft: 10,
                    alignSelf: 'flex-end',
                    fontWeight: 'medium',
                  }}
                >
                  Date: {formatDateUTC(tagData?.date_sent) || '-'}
                </Text>
              </View>

              {/* Horizontal Line */}
              <View style={[styles.horizontalLine, { marginBottom: 2 }]} />

              {/* Sample Type Header with Development Type */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 5,
                }}
              >
                <Text style={styles.sampleTypeHeader}>
                  {formatSampleTypeDisplay(sampleType).toUpperCase()}
                </Text>
                {developmentType && (
                  <Text style={styles.sampleSubType}>{developmentType}</Text>
                )}
              </View>

              {/* Product Details */}
              <InfoRow
                label="Vendor"
                value={
                  tagData?.company_name || tagData?.vendor_legal_name || '-'
                }
              />
              <WrappingInfoRow label="Style #" value={styleNumber || '-'} />
              <WrappingInfoRow
                label="Color/Wash"
                value={tagData?.color_wash || '-'}
              />
              <InfoRow label="Size" value={tagData?.sample_size || '-'} />
              <WrappingInfoRow
                label="Fabric Code & Name"
                value={tagData?.self?.fabric_code_name || '-'}
              />

              {/* Logistics fields - always shown in ProtoSamplePDF */}
              <InfoRow
                label="CC Lead Time"
                value={`${tagData?.custom_cut_lead_time || '-'} weeks (${formatDateUTC(tagData?.cc_lead_time_date) || '-'})`}
              />
              <InfoRow label="COO" value={tagData?.coo || '-'} />
              <InfoRow
                label="Shipment Mode"
                value={tagData?.shipment_mode || '-'}
              />

              <InfoRow
                label="FN Exclusive"
                value={tagData?.fn_exclusive ? 'Yes' : 'No'}
              />
              <InfoRow
                label="Fit Sample"
                value={tagData?.fit_sample ? 'Yes' : 'No'}
              />

              {/* Sample Made With Table */}
              <View style={{ marginTop: 4, marginBottom: 3 }}>
                <View style={styles.tableRow}>
                  <CompactTableCell
                    width="40%"
                    textStyle={{
                      ...styles.smallText,
                      fontWeight: 'semibold',
                      marginBottom: 6,
                    }}
                  >
                    Sample Made With:
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    textAlign="center"
                    textStyle={{
                      ...styles.headerText,
                      marginBottom: 6,
                    }}
                  >
                    Available
                  </CompactTableCell>
                  <CompactTableCell
                    width="30%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{
                      ...styles.headerText,
                      marginBottom: 6,
                    }}
                  >
                    Production
                  </CompactTableCell>
                </View>

                {SAMPLE_MADE_WITH_ITEMS.map((item) => (
                  <TableRow key={item}>
                    <CompactTableCell width="40%" textStyle={styles.cellText}>
                      {item
                        ? item.charAt(0).toUpperCase() +
                          item.slice(1).replace('_', ' ')
                        : ''}
                    </CompactTableCell>
                    <CheckMarkCell
                      hasCheckMark={
                        tagData?.sample_created_with?.[item] === 'Available'
                      }
                    />
                    <CheckMarkCell
                      hasBorder={false}
                      hasCheckMark={
                        tagData?.sample_created_with?.[item] === 'Production'
                      }
                    />
                  </TableRow>
                ))}
              </View>
            </View>

            {/* Right Column - Image and Barcode */}
            <View
              style={[styles.rightColumn, { justifyContent: 'space-between' }]}
            >
              {/* Product Image */}
              <ProductImage imageUrl={productImageUrl} />

              {/* Barcode */}
              <View style={{ marginTop: 8 }}>
                <PDFBarcode value={barcodeValue} />
              </View>

              {/* Cost and MOQ Table */}
              <View style={{ marginTop: 6, marginBottom: 3 }}>
                <View style={styles.tableRow}>
                  <CompactTableCell width="25%" textStyle={styles.headerText}>
                    {' '}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={styles.headerText}
                  >
                    Cost
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={styles.headerText}
                  >
                    MOQ
                  </CompactTableCell>
                </View>

                <TableRow>
                  <CompactTableCell width="25%" textStyle={styles.cellText}>
                    JR
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={styles.cellText}
                  >
                    {tagData?.junior_cost ? `$${tagData.junior_cost}` : ''}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={styles.cellText}
                  >
                    {tagData?.junior_moq || ''}
                  </CompactTableCell>
                </TableRow>

                <TableRow>
                  <CompactTableCell width="25%" textStyle={styles.cellText}>
                    Plus
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={styles.cellText}
                  >
                    {tagData?.plus_cost ? `$${tagData.plus_cost}` : ''}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={styles.cellText}
                  >
                    {tagData?.plus_moq || ''}
                  </CompactTableCell>
                </TableRow>
              </View>
            </View>
          </View>

          {/* Fabric Content & Weight Table */}
          <Text style={styles.sectionTitle}>Fabric Content & Weight:</Text>
          <View>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <CompactTableCell
                width="18%"
                textStyle={{
                  ...styles.headerText,
                  marginBottom: 6,
                }}
                padding={2}
              >
                Type
              </CompactTableCell>
              <CompactTableCell
                width="62%"
                textStyle={{ ...styles.headerText, marginBottom: 6 }}
                padding={2}
              >
                Content
              </CompactTableCell>
              <CompactTableCell
                width="20%"
                hasBorder={false}
                textAlign="right"
                textStyle={{ ...styles.headerText, marginBottom: 6 }}
                padding={2}
              >
                Weight (GSM)
              </CompactTableCell>
            </View>

            {/* Table Rows */}
            {getFabricSections(tagData).map((section, index, array) => {
              const isLastRow = index === array.length - 1;
              return (
                <View
                  key={section.type}
                  style={[
                    styles.tableRow,
                    { minHeight: 14 },
                    isLastRow
                      ? { borderBottomWidth: 0.5, borderBottomColor: '#000' }
                      : {},
                  ]}
                >
                  <CompactTableCell
                    width="18%"
                    textStyle={{
                      fontSize: 7,
                      backgroundColor: '#F8F8F8',
                      fontWeight: 'semibold',
                    }}
                    padding={6}
                  >
                    {section.type}
                  </CompactTableCell>
                  <CompactTableCell
                    width="62%"
                    textStyle={{ ...styles.smallText, marginBottom: 6 }}
                    padding={2}
                  >
                    {section.content}
                  </CompactTableCell>
                  <CompactTableCell
                    width="20%"
                    hasBorder={false}
                    textAlign="right"
                    textStyle={styles.cellText}
                    padding={6}
                  >
                    {section.weight}
                  </CompactTableCell>
                </View>
              );
            })}
          </View>

          {/* Comments Section - Two Column Layout */}
          <View style={styles.commentsSection}>
            {/* Left Column - Vendor Comments */}
            <View style={styles.commentsLeft}>
              <Text style={styles.sectionTitle}>Vendor Comments:</Text>
              <View style={{ height: 60 }}>
                <Text style={{ ...styles.smallText, padding: 1 }}>
                  {tagData?.comments}
                </Text>
              </View>
            </View>

            {/* Vertical divider line */}
            <View style={styles.verticalDivider} />

            {/* Right Column - FN Internal Only */}
            <View style={styles.commentsRight}>
              <Text style={styles.internalHeader}>FN INTERNAL ONLY</Text>

              {/* Horizontal line above Date Received */}
              <View
                style={{
                  height: 0.5,
                  backgroundColor: '#000',
                  marginTop: 24,
                  marginBottom: 3,
                }}
              />

              <Text style={{ fontSize: 7, fontWeight: 'medium' }}>
                Date Received:
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
